// cspell:ignore artifex
/**
 * License-usage metering for MuPDF (VP-0000-mupdf-usage-counter).
 *
 * Veritone's software license with Artifex for MuPDF requires the licensee to
 * record the number of documents processed with the software (agreement
 * Section 4.4) and prices the license per unique document per contract year
 * (Exhibit C). The browser runs MuPDF WebAssembly on every document opened in
 * the editor that has a PDF: the page-dimensions pre-pass opens it in the worker
 * on OPEN_DOCUMENT_SUCCESS unconditionally. So "document opened with a PDF" is
 * the browser-side unit of "document processed".
 *
 * The record is an ordinary `'redact'` audit event (see AUDIT_LOG_EVENTS.md),
 * emitted by `sagas/auditLogs.ts`, tagged with a top-level payload key so it can
 * be selected in the audit store without matching the human-readable ACTION
 * string. To report usage: count DISTINCT `tdoId` among events whose payload has
 * `meter === 'artifex-mupdf'` within the contract year.
 *
 * The event is deliberately kept OUT of the customer-facing audit CSV
 * (`uploadDocumentAuditLog` filters with {@link withoutLicenseMeterRecords}).
 * Document-open was never part of that trail, and this event exists for
 * Veritone's licensing obligation, not the customer's chain of custody.
 *
 * The payload deliberately carries NO `details` field. Since 2026-07-28 the
 * us-gov-2 production audit store has rejected every `'redact'` event that has
 * one (other events are stored normally), so a meter event with `details` was
 * never recorded there. Nothing reads `details` on this event: the counter and
 * the CSV filters match on the `meter` tag and `tdoId` only. Keep it out.
 */

/** Human-readable ACTION string for the meter event. */
export const LICENSE_METER_ACTION = 'Document processed';

/** Top-level payload key that marks a meter event. */
export const LICENSE_METER_KEY = 'meter';

/** Value of {@link LICENSE_METER_KEY} identifying the MuPDF license meter. */
export const LICENSE_METER_TAG = 'artifex-mupdf';

/**
 * Extra payload merged into the audit event by `serviceEmitAuditEvent`
 * (alongside `action`, `tdoId`, `userName`, `timestamp`). Only the meter tag:
 * see the header for why there is no `details`.
 */
export function licenseMeterPayload(): Record<string, unknown> {
  return {
    [LICENSE_METER_KEY]: LICENSE_METER_TAG,
  };
}

/** The minimum shape of an audit record the predicate needs. */
export interface MeterableAuditRecord {
  payload?: object | null;
}

/** True when `record` is a MuPDF license-meter event. */
export function isLicenseMeterRecord(record: MeterableAuditRecord): boolean {
  const payload = record.payload as Record<string, unknown> | null | undefined;
  return payload?.[LICENSE_METER_KEY] === LICENSE_METER_TAG;
}

/**
 * Drop meter events from a list of audit records, preserving order. Used before
 * the customer-facing audit CSV is formatted.
 */
export function withoutLicenseMeterRecords<T extends MeterableAuditRecord>(
  records: readonly T[]
): T[] {
  return records.filter((record) => !isLicenseMeterRecord(record));
}
